import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { X, Copy } from 'lucide-react';
import { categoriasAPI, tiposServicioAPI } from '../services/api';
import { selectStyles, EditableInput } from '../utils/adminShared';


const ServiciosPanel = () => {
    const [loading, setLoading] = useState(true);
    const [categorias, setCategorias] = useState([]);
    const [tiposServicio, setTiposServicio] = useState([]);
    const [nuevoTipo, setNuevoTipo] = useState({ nombre: '', descripcion: '', categoria_id: '' });
    const [nuevaCategoria, setNuevaCategoria] = useState({ nombre: '' });
    const [showCategoriasModal, setShowCategoriasModal] = useState(false);
    const [filtroCategoria, setFiltroCategoria] = useState(null);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [categoriasRes, tiposRes] = await Promise.all([
                categoriasAPI.getAll(true),
                tiposServicioAPI.getAll(true)
            ]);
            setCategorias(categoriasRes.data || []);
            setTiposServicio(tiposRes.data || []);
        } catch (err) {
            console.error('Error al cargar datos:', err);
        } finally {
            setLoading(false);
        }
    };

    // === CATEGORÍAS ===
    const agregarCategoria = async () => {
        if (!nuevaCategoria.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        try {
            await categoriasAPI.create(nuevaCategoria);
            setNuevaCategoria({ nombre: '' });
            cargarDatos();
            toast.success('Categoría creada');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const actualizarCategoria = async (id, data) => {
        try {
            await categoriasAPI.update(id, data);
            setCategorias(prev => prev.map(c => c.id === id ? { ...c, ...data } : c));
        } catch (err) {
            toast.error('Error: ' + err.message);
            cargarDatos();
        }
    };

    const eliminarCategoria = (id, nombre) => {
        toast((t) => (
            <div className="toast-confirm">
                <p>¿Eliminar la categoría <strong>{nombre}</strong>?</p>
                <div className="toast-confirm-buttons">
                    <button className="toast-btn toast-btn-cancel" onClick={() => toast.dismiss(t.id)}>
                        Cancelar
                    </button>
                    <button
                        className="toast-btn toast-btn-danger"
                        onClick={async () => {
                            toast.dismiss(t.id);
                            try {
                                await categoriasAPI.delete(id);
                                toast.success('Categoría eliminada');
                                cargarDatos();
                            } catch (err) {
                                toast.error('Error: ' + err.message);
                            }
                        }}
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        ), { duration: Infinity });
    };

    // === TIPOS DE SERVICIO ===
    const agregarTipoServicio = async () => {
        if (!nuevoTipo.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        if (!nuevoTipo.categoria_id) {
            toast.error('La categoría es obligatoria');
            return;
        }
        try {
            await tiposServicioAPI.create(nuevoTipo);
            setNuevoTipo({ nombre: '', descripcion: '', categoria_id: '' });
            cargarDatos();
            toast.success('Servicio creado');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const actualizarTipoServicio = async (id, data) => {
        try {
            await tiposServicioAPI.update(id, data);
            setTiposServicio(prev => prev.map(t => t.id === id ? { ...t, ...data } : t));
        } catch (err) {
            toast.error('Error: ' + err.message);
            cargarDatos();
        }
    };

    const eliminarTipoServicio = (id, nombre) => {
        toast((t) => (
            <div className="toast-confirm">
                <p>¿Eliminar el servicio <strong>{nombre}</strong>?</p>
                <div className="toast-confirm-buttons">
                    <button className="toast-btn toast-btn-cancel" onClick={() => toast.dismiss(t.id)}>
                        Cancelar
                    </button>
                    <button
                        className="toast-btn toast-btn-danger"
                        onClick={async () => {
                            toast.dismiss(t.id);
                            try {
                                await tiposServicioAPI.delete(id);
                                toast.success('Servicio eliminado');
                                cargarDatos();
                            } catch (err) {
                                toast.error('Error: ' + err.message);
                            }
                        }}
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        ), { duration: Infinity });
    };

    const duplicarServicio = async (tipo) => {
        try {
            await tiposServicioAPI.create({
                nombre: `${tipo.nombre} (copia)`,
                descripcion: tipo.descripcion || '',
                categoria_id: tipo.categoria_id || ''
            });
            cargarDatos();
            toast.success('Servicio duplicado');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const serviciosFiltrados = filtroCategoria
        ? tiposServicio.filter(t => t.categoria_id === filtroCategoria)
        : tiposServicio;

    if (loading) {
        return <div className="loading">Cargando servicios...</div>;
    }

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Servicios</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <h3 className="h3">Agregar Servicio</h3>
                    <button
                        className="btn ghost small"
                        type="button"
                        onClick={() => setShowCategoriasModal(true)}
                    >
                        Gestionar Categorías
                    </button>
                </div>
                <div className="form-row">
                    <label className="field">
                        <span className="label">Nombre *</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoTipo.nombre}
                            onChange={(e) => setNuevoTipo(n => ({ ...n, nombre: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Descripción</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoTipo.descripcion}
                            onChange={(e) => setNuevoTipo(n => ({ ...n, descripcion: e.target.value }))}
                        />
                    </label>
                    <div className="field">
                        <span className="label">Categoría *</span>
                        <Select
                            styles={selectStyles}
                            options={categorias.filter(c => c.activo).map(cat => ({
                                value: cat.id,
                                label: cat.nombre
                            }))}
                            value={
                                nuevoTipo.categoria_id
                                    ? { value: nuevoTipo.categoria_id, label: categorias.find(c => c.id === parseInt(nuevoTipo.categoria_id))?.nombre || '' }
                                    : null
                            }
                            onChange={(selected) => setNuevoTipo(n => ({ ...n, categoria_id: selected?.value || '' }))}
                            placeholder="Seleccionar categoría..."
                            isSearchable
                            noOptionsMessage={() => 'Sin opciones'}
                        />
                    </div>
                    <button className="btn neon" type="button" onClick={agregarTipoServicio}>
                        + Agregar
                    </button>
                </div>

                <div className="divider" />

                <div className="admin-section-header">
                    <h3 className="h3">Servicios Existentes ({serviciosFiltrados.length})</h3>
                    <Select
                        styles={selectStyles}
                        options={[
                            { value: null, label: 'Todas las categorías' },
                            ...categorias.filter(c => c.activo).map(cat => ({
                                value: cat.id,
                                label: cat.nombre
                            }))
                        ]}
                        value={
                            filtroCategoria
                                ? { value: filtroCategoria, label: categorias.find(c => c.id === filtroCategoria)?.nombre || '' }
                                : { value: null, label: 'Todas las categorías' }
                        }
                        onChange={(selected) => setFiltroCategoria(selected?.value || null)}
                        placeholder="Filtrar por categoría..."
                        isSearchable={false}
                        noOptionsMessage={() => 'Sin opciones'}
                    />
                </div>
                <div className="admin-list">
                    {serviciosFiltrados.map(tipo => (
                        <div key={tipo.id} className={`admin-item ${!tipo.activo ? 'inactive' : ''}`}>
                            <div className="admin-item-content">
                                <EditableInput
                                    type="text"
                                    value={tipo.nombre}
                                    onSave={(val) => actualizarTipoServicio(tipo.id, { nombre: val })}
                                />
                                <EditableInput
                                    type="text"
                                    value={tipo.descripcion || ''}
                                    onSave={(val) => actualizarTipoServicio(tipo.id, { descripcion: val })}
                                    placeholder="Descripción..."
                                />
                                <Select
                                    styles={selectStyles}
                                    options={[
                                        { value: '', label: 'Sin categoría' },
                                        ...categorias.filter(c => c.activo).map(cat => ({
                                            value: cat.id,
                                            label: cat.nombre
                                        }))
                                    ]}
                                    value={
                                        tipo.categoria_id
                                            ? { value: tipo.categoria_id, label: categorias.find(c => c.id === tipo.categoria_id)?.nombre || '' }
                                            : { value: '', label: 'Sin categoría' }
                                    }
                                    onChange={(selected) => actualizarTipoServicio(tipo.id, { categoria_id: selected?.value || null })}
                                    placeholder="Categoría..."
                                    isSearchable
                                    noOptionsMessage={() => 'Sin opciones'}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="admin-item-actions">
                                <button
                                    className="btn ghost small"
                                    onClick={() => duplicarServicio(tipo)}
                                    title="Duplicar servicio"
                                >
                                    <Copy size={14} />
                                </button>
                                <button
                                    className={`btn ghost small ${tipo.activo ? '' : 'success'}`}
                                    onClick={() => actualizarTipoServicio(tipo.id, { activo: !tipo.activo })}
                                >
                                    {tipo.activo ? 'Desactivar' : 'Activar'}
                                </button>
                                <button
                                    className="btn ghost small danger"
                                    onClick={() => eliminarTipoServicio(tipo.id, tipo.nombre)}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                    ))}
                    {serviciosFiltrados.length === 0 && (
                        <p className="empty-message">No hay servicios{filtroCategoria ? ' en esta categoría' : ''}</p>
                    )}
                </div>
            </div>
        </div>

        {/* Modal de Categorías */}
        {showCategoriasModal && (
            <div className="modal-overlay" onClick={() => setShowCategoriasModal(false)}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3 className="h3">Gestionar Categorías</h3>
                        <button
                            className="btn-icon"
                            onClick={() => setShowCategoriasModal(false)}
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="form-row">
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Nueva categoría *</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={nuevaCategoria.nombre}
                                    onChange={(e) => setNuevaCategoria(n => ({ ...n, nombre: e.target.value }))}
                                    placeholder="Nombre de la categoría"
                                />
                            </label>
                            <button
                                className="btn neon"
                                type="button"
                                onClick={agregarCategoria}
                                style={{ alignSelf: 'flex-end' }}
                            >
                                + Agregar
                            </button>
                        </div>

                        <div className="divider" />

                        <div className="admin-list">
                            {categorias.map(cat => (
                                <div key={cat.id} className={`admin-item ${!cat.activo ? 'inactive' : ''}`}>
                                    <div className="admin-item-content">
                                        <EditableInput
                                            type="text"
                                            value={cat.nombre}
                                            onSave={(val) => actualizarCategoria(cat.id, { nombre: val })}
                                        />
                                    </div>
                                    <div className="admin-item-actions">
                                        <button
                                            className={`btn ghost small ${cat.activo ? '' : 'success'}`}
                                            onClick={() => actualizarCategoria(cat.id, { activo: !cat.activo })}
                                        >
                                            {cat.activo ? 'Desactivar' : 'Activar'}
                                        </button>
                                        <button
                                            className="btn ghost small danger"
                                            onClick={() => eliminarCategoria(cat.id, cat.nombre)}
                                        >
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {categorias.length === 0 && (
                                <p className="empty-message">No hay categorías creadas</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>
    );
};

export default ServiciosPanel;
